/**
 * Rotation: begin, commit and abort (#1694, spec section 6.12).
 *
 * ⚠️ THE MOST DANGEROUS OPERATION IN THIS DESIGN, and the plan says so. Commit
 * revokes every other device the member has and clears the wrapping each of
 * them holds; it cannot be undone, and it is the point at which MK1 stops
 * being recoverable. Everything below is ordered around the crashes, because a
 * rotation that stops half way is the ordinary case, not the exotic one — the
 * re-seal walks every note, highlight, tag and blank answer the member owns.
 *
 * ── THE ORDER, AND WHAT EACH ORDER COSTS ───────────────────────────────────
 *
 * BEGIN keeps MK2 LOCALLY FIRST, then tells the server. A crash between the
 * two leaves a device holding a generation the server has never heard of:
 * harmless, because every row is still on MK1 and the server still accepts
 * writes under it, and the next launch drops the orphan. The other order
 * cannot be recovered from at all — `rotate/begin` first would leave the
 * server with `pendingKeyId` set and NO device anywhere holding MK2, so
 * neither commit nor abort could ever be signed (both are signed by the
 * pending key) while `EncryptionModeGuard` refuses every write until one of
 * them is. A crash in a one-line window would lock the member out of writing
 * for good.
 *
 * COMMIT tells the server FIRST, then promotes locally. The server is what
 * makes MK1 unrecoverable; a device that dropped MK1 first and then failed to
 * commit could not read a single one of its own rows, every one of which is
 * still on MK1.
 *
 * ABORT re-seals back to MK1, tells the server, and only then drops MK2 —
 * dropping it first would throw away the key the abort has to be signed with.
 *
 * ── WHAT MAKES IT RESUMABLE ────────────────────────────────────────────────
 *
 * Nothing here keeps a cursor or a list of what it has done. The question
 * "what is left" is asked of the server every time, by the same count
 * `rotate/commit` refuses on (`rotation.remaining`, api#1136), so progress and
 * predicate cannot disagree and a resume cannot believe itself finished. The
 * only local state is the pending generation in the key store, and the only
 * thing that reads it is the decision below about which device may continue.
 */
import {
  createWrappedSigningKey,
  generateMasterKey,
  wrapMasterKeyToDevice,
} from '@/utils/courses-crypto';
import { createPendingSignerFromStore, createSignerFromStore } from './store-signer';
import { autosave } from '@/utils/courses-autosave';

export const ROTATION_ERRORS = Object.freeze({
  /** This device holds no key, so it cannot start or continue a rotation. */
  NO_KEY: 'NO_KEY',
  /** A rotation is running and this device is not the one that started it. */
  NOT_THIS_DEVICE: 'NOT_THIS_DEVICE',
  /** Rows are still on the generation the operation is trying to leave behind. */
  ROWS_REMAIN: 'ROWS_REMAIN',
  /** There is no rotation to commit or abort. */
  NOT_ROTATING: 'NOT_ROTATING',
  /** A rotation is already pending; it has to be finished or cancelled before another begins. */
  ALREADY_ROTATING: 'ALREADY_ROTATING',
});

export class RotationError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'RotationError';
    this.code = code;
  }
}

const DEFAULT_DEPS = Object.freeze({
  generateMasterKey,
  createWrappedSigningKey,
  wrapMasterKeyToDevice,
  createSigner: createSignerFromStore,
  createPendingSigner: createPendingSignerFromStore,
  flushAutosave: () => autosave.flush(),
});

const withDeps = overrides => (overrides ? { ...DEFAULT_DEPS, ...overrides } : DEFAULT_DEPS);

/**
 * Begin a rotation (spec 6.12 step 1).
 *
 * `pendingRecoveryWrapping` and `pendingPassphraseWrapping` are the caller's,
 * not this module's: MK2 has to be wrapped under a recovery code the member is
 * shown and under a passphrase only they can type, and both of those are
 * screens. The API refuses a begin that omits either while the member has one
 * (api#1138, decision 51), so a caller that forgets is answered by the server
 * rather than by a silent loss.
 *
 * @param {object} args
 * @param {object} args.store the root Vuex store
 * @param {object} [args.pendingRecoveryWrapping] MK2 under the NEW recovery code
 * @param {object} [args.pendingPassphraseWrapping] MK2 under the member's passphrase
 * @param {object} [args.pendingPreviousWrapping] MK1 under the NEW recovery code
 * @param {object} [args.deps]
 * @returns {Promise<{ pendingKeyId: string }>}
 */
export async function beginRotation({
  store,
  pendingRecoveryWrapping,
  pendingPassphraseWrapping,
  pendingPreviousWrapping,
  keepDeviceIds = [],
  deps: overrides,
} = {}) {
  if (!store) throw new TypeError('beginRotation needs the root Vuex store');
  const deps = withDeps(overrides);

  const material = store.getters['courses-keys/keyMaterial'];
  if (!material?.mk) {
    throw new RotationError(ROTATION_ERRORS.NO_KEY, 'This device holds no key to rotate from');
  }
  /*
   * ⚠️ ONE ROTATION AT A TIME, CHECKED HERE AND NOT ONLY ON THE SERVER. The
   * server refuses a second begin (its `pendingKeyId: null` WHERE) — but by the
   * time it had, `keepPendingGeneration` below had already written the new
   * generation over the one this device was holding, and the refusal then
   * dropped it: MK2 gone from the only phone that had it, every row the walk
   * had moved unreadable there, nothing left able to finish or cancel
   * (2026-09-10). So the status is re-read and the begin refused BEFORE a key
   * is generated or kept. The screen turns this state into "continue".
   */
  await store.dispatch('courses-devices/refreshStatus').catch(() => {});
  if (store.getters['courses-devices/pendingKeyId']) {
    throw new RotationError(
      ROTATION_ERRORS.ALREADY_ROTATING,
      'A rotation is already in progress; continue or cancel it rather than starting another',
    );
  }
  const memberId = store.getters['courses-keys/memberId'];
  const deviceId = store.getters['courses-devices/localDeviceId'];

  // Anything still queued goes NOW, under the generation it was sealed with.
  // Commit revokes every other device and makes MK1 unrecoverable, so an entry
  // left here would be dropped unopened on the next drain. Bounded inside the
  // engine, and never able to fail the rotation: a write that cannot be sent is
  // the outbox's problem, not this operation's.
  try {
    await deps.flushAutosave();
  } catch (error) {
    console.warn('[CoursesE2EE] The autosave queue could not be flushed before rotating:', error);
  }

  const { keyId: pendingKeyId, mk: pendingMk } = deps.generateMasterKey();
  const signing = await deps.createWrappedSigningKey({
    mk: pendingMk,
    keyId: pendingKeyId,
    memberId,
  });

  // ⚠️ LOCAL FIRST. See the header: the other order is the one crash this
  // design cannot recover from.
  await store.dispatch('courses-keys/keepPendingGeneration', {
    keyId: pendingKeyId,
    mk: pendingMk,
    // Which devices the commit keeps (api#1156), held beside the generation so
    // a relaunch resumes the sign-out the member actually chose.
    keepDeviceIds,
  });

  const signer = await deps.createSigner(store);
  try {
    await store.dispatch('courses-devices/beginRotation', {
      pendingKeyId,
      pendingSigningKey: signing.publicKey,
      pendingSigningKeyWrapped: signing.wrapping,
      pendingRecoveryWrapping,
      pendingPassphraseWrapping,
      pendingPreviousWrapping,
      deviceId,
      // The member's choice goes to the SERVER too (api#1158), so whichever
      // device finishes this rotation is held to it. Only when there is one.
      ...(keepDeviceIds.length ? { keepDeviceIds } : {}),
      signer,
    });
  } catch (error) {
    // ⚠️ A THROW IS NOT PROOF THE SERVER REFUSED. A timeout, a dropped
    // connection, a 5xx after the write, or a `stale-nonce` retry landing on
    // a begin the first attempt had already committed — all of these throw
    // here with the pending generation STORED. Dropping MK2 on the strength of
    // an error alone is the one unrecoverable mistake in this whole design: the
    // bundle would name a pending generation, `EncryptionModeGuard` would
    // refuse every write until it is resolved, and neither commit nor abort
    // could ever be signed, because both are signed by the pending key that was
    // wrapped under the MK2 just thrown away. No recovery code reaches it and
    // no other device holds it.
    //
    // So the server is asked, exactly as the header says progress is asked
    // everywhere else here. It is dropped only when the server is reached AND
    // says it is holding no such generation.
    await dropPendingIfServerNeverTookIt(store, pendingKeyId);
    throw error;
  }

  return { pendingKeyId };
}

/**
 * Commit (spec 6.12 step 3). ⚠️ Revokes every other device, and cannot be
 * undone.
 *
 * The count is read from the SERVER rather than from what the walk believes it
 * wrote: the walk saw a page at a time and the member's other devices were
 * live throughout, and a row left on the old generation after the promotion
 * would be unreadable by anyone for ever. The server checks it again inside
 * its own transaction, so this check is a courtesy that keeps the member out
 * of a 409 they cannot act on — not the guarantee.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<void>}
 */
export async function commitRotation({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('commitRotation needs the root Vuex store');
  const deps = withDeps(overrides);

  const pendingKeyId = requireRotating(store);

  // ⚠️ READ AGAIN BEFORE READING THE COUNT. `rotationRemaining` comes off the
  // last status response, and the walk that just finished is precisely what
  // makes that response out of date — it moved every row after the number was
  // fetched. Guarding on it unrefreshed is a commit refused on a count that
  // was already stale when it was read, and the member is left holding a
  // rotation nothing will finish (COURSES-E2EE-014, 2026-09-08).
  //
  // A courtesy that fires on a stale number is worse than no courtesy: the
  // server re-checks inside its own transaction either way, so the only thing
  // this check can add is keeping the member out of a 409 they cannot act on
  // — and it can only do that if it is asking about now.
  await store.dispatch('courses-devices/refreshStatus').catch(() => {});
  if (store.getters['courses-devices/rotationRemaining'] > 0) {
    throw new RotationError(
      ROTATION_ERRORS.ROWS_REMAIN,
      'Some items are still sealed under the old key',
    );
  }

  const keep = await wrappingsForKeptDevices(store, deps);
  const asked = store.getters['courses-keys/pendingKeepDeviceIds'] ?? [];
  // Said out loud, because "signed out one device, lost them all" reads the
  // same on the phone whether the commit kept nobody or the kept device failed
  // to re-key (2026-09-11). Ids, never names.
  console.warn(
    `[CoursesE2EE] rotate/commit keeps ${keep.length} of ${asked.length} device(s): ` +
      `${keep.map(k => k.deviceId).join(', ') || 'none'}`,
  );
  const signer = await deps.createPendingSigner(store);
  await store.dispatch('courses-devices/commitRotation', { pendingKeyId, signer, keep });

  // Only now. The server is what makes MK1 unrecoverable, and a device that
  // dropped it first and then failed to commit could read none of its own
  // rows. A crash between the two is put right by the resume path, which
  // promotes on seeing the server's committed keyId match the pending one.
  await store.dispatch('courses-keys/promoteGeneration');
  return { kept: keep.map(k => k.deviceId), asked: [...asked] };
}

/**
 * MK2 wrapped to each device the pending rotation keeps (api#1156): the
 * commit hands these to the server, which writes them in the same transaction
 * that revokes everyone else. Wrapped to the device's registered public key
 * with no approval secret — that device is already approved; what it needs is
 * the new key, not a second approval.
 *
 * A kept device that is no longer approved (denied or signed out meanwhile)
 * is left out rather than failing the commit: the server would refuse the
 * whole commit for it, and the member's choice about the OTHER kept devices
 * still stands.
 */
async function wrappingsForKeptDevices(store, deps) {
  // The server's list first (api#1158): it is what the commit is held to, and
  // it is all a device that did not begin this rotation has. The local list
  // is the same choice, kept for a server that predates the column.
  const server = store.getters['courses-devices/rotation']?.keepDeviceIds;
  const ids =
    Array.isArray(server) && server.length
      ? server
      : (store.getters['courses-keys/pendingKeepDeviceIds'] ?? []);
  if (ids.length === 0) return [];
  const pending = store.getters['courses-keys/pendingKeyMaterial'];
  const memberId = store.getters['courses-keys/memberId'];
  const own = store.getters['courses-devices/localDeviceId'];
  const approved = (store.getters['courses-devices/devices'] ?? []).filter(
    device => device.status === 'APPROVED' && device.id !== own && ids.includes(device.id),
  );
  return Promise.all(
    approved.map(async device => ({
      deviceId: device.id,
      wrappedMk: await deps.wrapMasterKeyToDevice({
        mk: pending.mk,
        keyId: pending.keyId,
        memberId,
        devicePublicKey: device.publicKey,
      }),
    })),
  );
}

/**
 * Ask the server to start unwinding the rotation (spec 6.12 step 4a, api#1148).
 *
 * ⚠️ WHAT THIS BUYS IS THE RIGHT TO WRITE BACKWARDS. The write gate accepts
 * only the pending generation while a rotation runs — that is what makes the
 * forward walk converge — and it is also why an abort is impossible the moment
 * one row has moved: the server refuses to drop a generation that still has
 * rows on it, and nothing may put them back. From here the gate is inverted,
 * so the walk can empty the pending generation the same way it filled it.
 *
 * ONE-WAY. There is no route back to rotating, and after this commit is refused
 * — so the caller has decided, not merely started thinking about it.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<void>}
 */
export async function beginAbortRotation({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('beginAbortRotation needs the root Vuex store');
  const deps = withDeps(overrides);

  const pendingKeyId = requireRotating(store);
  const signer = await deps.createPendingSigner(store);
  await store.dispatch('courses-devices/abortBeginRotation', { pendingKeyId, signer });
}

/**
 * Abort (spec 6.12 step 4). The cheap way out: nothing is revoked and no
 * version moves.
 *
 * The rows have to be put BACK first — the server refuses an abort while any
 * row is still on the pending generation, because dropping it then would
 * strand them under a key no device the bundle names would hold.
 *
 * @param {{ store: object, deps?: object }} args
 * @returns {Promise<void>}
 */
export async function abortRotation({ store, deps: overrides } = {}) {
  if (!store) throw new TypeError('abortRotation needs the root Vuex store');
  const deps = withDeps(overrides);

  const pendingKeyId = requireRotating(store);
  const signer = await deps.createPendingSigner(store);
  await store.dispatch('courses-devices/abortRotation', { pendingKeyId, signer });

  // After the server, for the same reason commit promotes after: this is the
  // key the abort was signed with, and throwing it away first would leave a
  // failed abort with nothing able to make a second attempt.
  await store.dispatch('courses-keys/dropPendingGeneration');
}

/**
 * What this device should do about the rotation the server reports, if
 * anything (spec 6.12, and the four interruption points #1694 names).
 *
 * It is a decision and not an action: every branch is named so a caller — a
 * launch check, a screen — can act on it and a spec can assert it, rather than
 * inferring intent from side effects.
 *
 * @param {object} store the root Vuex store
 * @returns {{ action: string, pendingKeyId?: string, remaining?: number }}
 */
export const ROTATION_RESUME = Object.freeze({
  /** Nothing is happening, and this device holds nothing left over. */
  NONE: 'none',
  /** The server committed while this device was away: adopt MK2 as current. */
  PROMOTE: 'promote',
  /** This device holds a generation no rotation exists for: forget it. */
  DROP: 'drop',
  /** This device is the one rotating, and rows are still to re-seal. */
  CONTINUE: 'continue',
  /** This device is the one rotating and everything is re-sealed: commit. */
  COMMIT: 'commit',
  /** A rotation is running that this device cannot continue. Read-only. */
  WAIT: 'wait',
});

export function resumeStateFor(store) {
  const pendingLocal = store.getters['courses-keys/pendingKeyMaterial'];
  const pendingServer = store.getters['courses-devices/pendingKeyId'];
  const committedServer = store.getters['courses-devices/keyId'];

  if (!pendingServer) {
    if (!pendingLocal) return { action: ROTATION_RESUME.NONE };
    // The server has no rotation and its committed generation IS the one this
    // device was holding as pending: the commit landed and the crash was
    // between the server's answer and the local promotion.
    if (pendingLocal.keyId === committedServer) {
      return { action: ROTATION_RESUME.PROMOTE, pendingKeyId: pendingLocal.keyId };
    }
    // A begin the server never took, or an abort that landed. Either way this
    // generation belongs to nothing.
    return { action: ROTATION_RESUME.DROP, pendingKeyId: pendingLocal.keyId };
  }

  // A rotation is running. Only the device holding MK2 can move it, and a
  // device holding a DIFFERENT pending generation is holding an orphan from an
  // earlier attempt — it waits like any other, rather than signing for a
  // generation it has never seen.
  if (!pendingLocal || pendingLocal.keyId !== pendingServer) {
    return { action: ROTATION_RESUME.WAIT, pendingKeyId: pendingServer };
  }

  const remaining = store.getters['courses-devices/rotationRemaining'];
  return {
    action: remaining > 0 ? ROTATION_RESUME.CONTINUE : ROTATION_RESUME.COMMIT,
    pendingKeyId: pendingServer,
    remaining,
  };
}

/**
 * Drop the just-created pending generation, but ONLY on the server's word.
 *
 * Silence is not a no: if the status cannot be refreshed, the generation is
 * kept and `resumeStateFor` decides on the next launch with a fresh answer —
 * a device holding an orphan MK2 is harmless (every row is still on MK1, and
 * the server still accepts writes under it), whereas a device that dropped a
 * live MK2 has locked the member out of writing for good.
 */
async function dropPendingIfServerNeverTookIt(store, pendingKeyId) {
  const refreshed = await store
    .dispatch('courses-devices/refreshStatus')
    .then(() => true)
    .catch(() => false);
  if (!refreshed) return;
  if (store.getters['courses-devices/pendingKeyId'] === pendingKeyId) return;
  await store.dispatch('courses-keys/dropPendingGeneration').catch(() => {});
}

function requireRotating(store) {
  const pendingKeyId = store.getters['courses-devices/pendingKeyId'];
  if (!pendingKeyId) {
    throw new RotationError(ROTATION_ERRORS.NOT_ROTATING, 'No rotation is in progress');
  }
  const held = store.getters['courses-keys/pendingKeyMaterial'];
  if (!held || held.keyId !== pendingKeyId) {
    // Signing would fail at the API, and the message there would blame the
    // key rather than the device. Saying it here is the difference between
    // "this phone cannot finish it" and "your key is wrong".
    throw new RotationError(
      ROTATION_ERRORS.NOT_THIS_DEVICE,
      'This device is not the one running the rotation',
    );
  }
  return pendingKeyId;
}
